/**
 * Confirmation page reached from the recap / morning-briefing "Mark done" link in email. The
 * action-item-action edge function does the actual mutation server-side, then 302s here with
 * `?status=…&title=…` so the rendering happens in the React app (some email-client webviews
 * mangle a direct text/html response from the function).
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const STATUSES: Record<
  string,
  { title: string; heading: string; message: string; tone: 'ok' | 'error' }
> = {
  ok: {
    title: 'Marked done',
    heading: 'Done. Nice work.',
    message: 'is now marked as done in Upstand.',
    tone: 'ok',
  },
  reopened: {
    title: 'Reopened',
    heading: 'Reopened',
    message: 'is back on the open list. It will show under outstanding items in the next meeting email.',
    tone: 'ok',
  },
  expired: {
    title: 'Link expired',
    heading: 'Link expired',
    message:
      "That link has expired or been tampered with. Open the action item in Upstand and mark it done from there.",
    tone: 'error',
  },
  not_found: {
    title: 'Not found',
    heading: 'Action item not found',
    message: 'It may have been deleted from Upstand.',
    tone: 'error',
  },
  not_allowed: {
    title: 'Not allowed',
    heading: "This link can't act on that item",
    message: 'Open Upstand and mark it done from there.',
    tone: 'error',
  },
  archived: {
    title: 'Archived',
    heading: 'That item was archived',
    message:
      'Someone cleared it off the list, so this link no longer does anything. You can reopen it from the action items page.',
    tone: 'error',
  },
  error: {
    title: 'Something went wrong',
    heading: 'Something went wrong',
    message: 'Open Upstand and try again from the action items page.',
    tone: 'error',
  },
};

export function ActionItemDonePage() {
  const params = new URLSearchParams(window.location.search);
  const status = (params.get('status') || 'ok').toLowerCase();
  const title = params.get('title') || '';
  const copy = STATUSES[status] || STATUSES.error;
  const message = copy.tone === 'ok' && title ? `“${title}” ${copy.message}` : copy.message;
  const Icon = copy.tone === 'ok' ? CheckCircle2 : AlertCircle;
  const accentClass = copy.tone === 'ok' ? 'text-emerald-700' : 'text-rose-600';

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className={`mb-2 flex items-center gap-2 ${accentClass}`}>
            <Icon className="h-5 w-5" />
            <CardTitle>{copy.heading}</CardTitle>
          </div>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full">
            <a href="/">Open Upstand</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
